import React, { useState } from 'react';
import axios from 'axios';
import { Button, Tooltip, useToast } from '@chakra-ui/react';
import { FiSend } from 'react-icons/fi';
import SendDocumentButton from './forms/SendDocumentButton';
import { ProposalStatus } from '../models/proposal';

// item 576 — THE one "send a proposal to the client" flow, shared by every entry point.
// The grid row menu and the proposal form footer previously diverged: the form's Send was a
// bare SendDocumentButton that never minted the public accept link the grid attached (while
// its copy still promised "the link below"). Now both render this control: a PENDING proposal
// mints (or reuses) its public approval token via POST proposal/SendForApproval/{id} and
// carries the accept link as the email's action button (items 227/550); any other status
// sends the plain document. The status-aware subject/body come from the server copy rule
// (GET email/copy → DocumentEmailCopy) via SendDocumentButton, so an approved proposal says
// it's approved and a pending one keeps the review/approve framing.
const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

interface Props {
  proposalId: string;
  status?: ProposalStatus;
  // item 550 — render ONLY the hidden dialog and open it by bumping the signal (grid rows);
  // the default renders a visible Send button (form footer).
  hideTrigger?: boolean;
  autoOpenSignal?: number;
  // item 870 (Chad) — when set, the Send button is disabled and this explains why (e.g. a
  // customer request must be assigned to a real project before it can be priced-and-sent).
  disabledReason?: string;
  // item 1031 (Chad) — an UNSENT customer request gets a special trigger: sending is also the
  // moment pricing publishes to the customer's portal (SentDate unlocks their priced PDF), so
  // the button says so. Reverts to the plain "Send" once SentDate is stamped.
  publishToPortal?: boolean;
}

const ProposalSendControl: React.FC<Props> = ({
  proposalId, status, hideTrigger, autoOpenSignal, disabledReason, publishToPortal,
}) => {
  const toast = useToast();
  const [acceptLink, setAcceptLink] = useState<string | undefined>();
  const [innerSignal, setInnerSignal] = useState(0);

  const openSend = async () => {
    let link: string | undefined;
    if (status === ProposalStatus.Pending) {
      try {
        const res = await axios.post<{ publicPath: string }>(`${apiUrl()}proposal/SendForApproval/${proposalId}`);
        // item 555 — the client's approval link MUST point at the deployed public site, never
        // window.location.origin: a link built on localhost:3000 (local testing) is dead for
        // the client. REACT_APP_PUBLIC_BASE_URL is the production public origin (falls back to
        // origin only if unset, e.g. a one-off preview build).
        const publicBase = process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin;
        link = `${publicBase}${res.data.publicPath}`;
      } catch (e: any) {
        toast({
          status: 'error',
          title: 'Could not prepare the client approval link',
          description: e?.response?.data ?? 'Try again.',
        });
        return;
      }
    }
    setAcceptLink(link);
    setInnerSignal((s) => s + 1);
  };

  // grid rows open the dialog programmatically by bumping the outer signal
  React.useEffect(() => {
    if (autoOpenSignal && autoOpenSignal > 0 && proposalId) { openSend(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenSignal]);

  return (
    <>
      {!hideTrigger && (
        // item 870 — a blocked Send explains itself instead of silently working on the wrong home
        // item 1031 — on an unsent customer request the trigger reads "Send & publish to portal"
        // (with a tooltip saying what publishes) so it's explicit that THIS send releases pricing.
        <Tooltip
          label={disabledReason
            || (publishToPortal
              ? 'Emails the priced proposal AND unlocks the priced PDF in the customer’s portal.'
              : undefined)}
          isDisabled={!disabledReason && !publishToPortal}
        >
          {/* item 1033 — the pivotal action stands out: publish-to-portal renders as the
              footer's ONE solid brand button (everything else there is ghost) */}
          <Button
            size="sm"
            variant={publishToPortal ? undefined : 'ghost'}
            leftIcon={<FiSend />}
            onClick={openSend}
            isDisabled={!!disabledReason}
          >
            {publishToPortal ? 'Send & publish to portal' : 'Send'}
          </Button>
        </Tooltip>
      )}
      <SendDocumentButton
        hideTrigger
        documentType="Proposal"
        documentId={proposalId}
        autoOpenSignal={innerSignal}
        extraLink={acceptLink ? { label: 'Approve proposal', url: acceptLink } : undefined}
      />
    </>
  );
};

ProposalSendControl.defaultProps = {
  status: undefined,
  hideTrigger: false,
  autoOpenSignal: undefined,
  disabledReason: undefined,
  publishToPortal: false,
};

export default ProposalSendControl;
